import { useState, FormEvent, ComponentType } from 'react';

import dynamic from 'next/dynamic';

import { Text, Avatar, Button, Toast, Flexbox, ClearButton } from '@morressier/ts';

import { Grid, useMediaQuery, useTheme } from '@material-ui/core';
import debounce from 'lodash/debounce';
import { toast } from 'react-toastify';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';

import { DiscussionsResponse } from 'api/discussions';
import {
  deleteRtmpDiscussion,
  DiscussionsSortOrder,
  getRtmpSessionDiscussionsEndpoint,
  postRtmpDiscussions,
} from 'api/livestream-rtmp';
import { Comment } from 'components/Comment';
import { MentionsEditorProps, MentionsEvent } from 'components/Comment/MentionsEditor';
import { FeatureFlags } from 'config/featureFlags';
import { SimpleCard } from 'containers/SessionsList/styles';
import { ThemeOverides } from 'containers/styles';
import { useRegAndTicketingContext } from 'store/modules/eventTicketing';
import { PusherHandler } from 'store/modules/pusher';
import {
  SESSION_DISCUSSION_PRESENTATION_DELETED,
  SESSION_DISCUSSION_PRESENTATION_UPDATED,
} from 'utils/pusher';

import { SessionBlockedQnA } from '../SignUpBanner';
import { SimpleDropdown } from '../SimpleDropdown';
import * as Styles from '../styles';

const MentionsEditor: ComponentType<MentionsEditorProps> = dynamic(
  () => import('components/Comment/MentionsEditor'),
  { ssr: false },
);

const LIMIT = 10;

export const SessionComments: React.FC<{
  sessionId: string;
  authUser?: MorressierUserProfile;
  canApprove?: boolean;
  speakers?: SessionPosterUser[];
  auto_approve_discussions?: boolean;
  isMainEditorFocused?: boolean;
  isHappeningNow: boolean;
}> = props => {
  const [comment, setComment] = useState<string>('');
  const [rawContent, setRawContent] = useState<string>('');
  const [sort_order, set_sort_order] = useState<DiscussionsSortOrder>(
    DiscussionsSortOrder.Ascending,
  );

  const { authUser, canApprove, speakers, isMainEditorFocused = false } = props;

  const getKey = (pageIndex: number, _previousPageData: DiscussionsResponse) => [
    getRtmpSessionDiscussionsEndpoint(props.sessionId, {
      sort_order,
      offset: LIMIT * pageIndex,
      limit: LIMIT,
    }),
  ]; // SWR key
  const {
    data: discussionsResponse,
    size,
    setSize,
    mutate: boundMutate,
  } = useSWRInfinite<DiscussionsResponse>(getKey, { revalidateOnFocus: false });

  const { data: taggableUsers } = useSWR<TaggableUser[]>([
    `api/v3/discovery/sessions/${props.sessionId}/hybrid/discussions/taggable-users`,
  ]);

  const mentions = taggableUsers
    ? taggableUsers.map(author => ({
        name: author.full_name || 'No name',
        avatar: author.picture_url,
        id: author.id,
        department: author.department,
        organization: author.organization,
      }))
    : [];

  const sessionDiscussionPresentationUpdated = SESSION_DISCUSSION_PRESENTATION_UPDATED(
    props.sessionId,
  );

  const sessionDiscussionPresentationDeleted = SESSION_DISCUSSION_PRESENTATION_DELETED(
    props.sessionId,
  );

  const checkSpeaker = (id: string) => speakers?.some(speaker => speaker?.id === id);

  const getChipName = (id: string) => (checkSpeaker(id) ? 'Speaker' : '');
  const [postingComment, setPostingComment] = useState(false);

  const [resetEditor, setResetEditor] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const [mentionedUsersState, setMentionedUsersState] = useState<string[]>([]);

  const discussionsAPIResponse = discussionsResponse ? [...discussionsResponse] : [];

  /**
   * This method helps to clear the editor when a user submits the form
   * If a better way is found we can remove or update this method
   */
  const clearEditor = () => {
    setComment('');
    setMentionedUsersState([]);
    setResetEditor(true);
    setTimeout(() => {
      setResetEditor(false);
    });
  };

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    if (postingComment) return;

    try {
      setPostingComment(true);

      await postRtmpDiscussions(props.sessionId, {
        session: props.sessionId,
        content: comment?.trim(),
        raw_content: rawContent.trim(),
        mentions: mentionedUsersState,
      });

      setPostingComment(false);
      setComment('');
      clearEditor();

      /**
       * only show the toast to people who can't approve
       * and when auto_approve_discussions is not enabled
       */
      if (props.auto_approve_discussions) return;
      if (!canApprove) {
        toast(() => (
          <Toast title="Your message has been sent, but it needs a moderators approval" />
        ));
      }
    } catch (error) {
      setPostingComment(false);
      toast(() => <Toast title="Sorry, we couldn't post your comment." />);
    }
  };

  const handleReply = async (
    parent_id: string,
    message: string,
    rawMessage: string,
    mentionedUsers: string[],
  ) => {
    try {
      await postRtmpDiscussions(props.sessionId, {
        session: props.sessionId,
        parent: parent_id,
        content: message?.trim(),
        raw_content: rawMessage.trim(),
        mentions: mentionedUsers,
      });

      return true;
    } catch (error) {
      toast(() => <Toast title="Sorry, we couldn't post your comment." />);
      return false;
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteRtmpDiscussion(props.sessionId, id);
      return true;
    } catch (error) {
      toast(() => <Toast title="Sorry, we couldn't delete this comment." />);
      return false;
    }
  };

  const handleContentChange = (e: MentionsEvent) => {
    if (e.rawString === '') {
      return setComment(e.rawString);
    }

    setComment(e.stringifiedEditorContent);
    setMentionedUsersState(e.mentions);
    return setRawContent(e.rawString);
  };

  const totalDescussionsLoaded = discussionsAPIResponse.reduce(
    (acc, ele) => ele.discussions.length + acc,
    0,
  );

  const shouldRenderLoadMoreButton =
    discussionsAPIResponse &&
    discussionsAPIResponse.length > 0 &&
    totalDescussionsLoaded < discussionsAPIResponse[0].total;

  const { breakpoints } = useTheme();
  const isExtraSmallDevice = useMediaQuery(breakpoints.down('xs'));

  const handlePusherUpdates = () => boundMutate();
  const { state } = useRegAndTicketingContext();

  const isTicketingFlagEnabled = FeatureFlags.Event_Ticketing.isEnabled();

  const canUserPostComment = () => {
    if (!isTicketingFlagEnabled) return !!authUser;

    return state.isTicketedEvent ? !!authUser && !!state.userHasAccess : !!authUser;
  };

  return (
    <SimpleCard color="white" style={{ padding: '24px', cursor: 'unset', marginBottom: '3.5rem' }}>
      <Grid container spacing={2} className="mb2">
        <Grid item xs={12} sm={8}>
          <Text fontWeight="bold">Ask a Question</Text>
          <Text color="grey">Use '@' to tag a Speaker by name.</Text>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Flexbox
            alignItems="center"
            justifyContent={isExtraSmallDevice ? 'flex-start' : 'flex-end'}
            style={{ height: '100%' }}
          >
            <SimpleDropdown
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              dropdownContainerStyle={{ minWidth: 'calc(100% + 1rem)', marginLeft: '-0.75rem' }}
              dropdownOptions={[
                { value: 'asc', label: 'All Questions' },
                { value: 'desc', label: 'Most Recent' },
              ]}
              onDropdownChange={({ value }: { value: 'asc' | 'desc'; label: string }) => {
                if (value === 'asc') {
                  return set_sort_order(DiscussionsSortOrder.Ascending);
                }

                return set_sort_order(DiscussionsSortOrder.Descending);
              }}
            >
              Ask a question
            </SimpleDropdown>
          </Flexbox>
        </Grid>
      </Grid>
      {canUserPostComment() && authUser ? (
        <Styles.FlexContainer className="mt4">
          <Avatar
            customSize="2.5rem"
            customFontSize="1.1rem"
            fullName={authUser.full_name}
            src={authUser.picture_url}
            style={{ margin: '0 0.5rem 0 0' }}
          />
          <Styles.FlexColumn as="form" onSubmit={handleCreate} style={{ width: '100%' }}>
            <ThemeOverides color={{ primaryBrand: '#424d5d' }}>
              <MentionsEditor
                style={{ marginTop: 0, minHeight: '55px' }}
                rows={3}
                placeholder="Write a comment..."
                mentions={mentions}
                onChange={handleContentChange}
                reset={resetEditor}
                handleFocus={() => setIsFocused(true)}
                handleBlur={() => setIsFocused(false)}
                externalScroll={isMainEditorFocused}
              />
            </ThemeOverides>

            {(isFocused || comment?.trim().length > 0) && (
              <Flexbox justifyContent="flex-end">
                <Button
                  style={{ marginRight: '0.5rem' }}
                  type="button"
                  onClick={() => {
                    clearEditor();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  style={{ alignSelf: 'flex-end' }}
                  color="primary"
                  loading={postingComment}
                  disabled={comment?.trim().length <= 0}
                >
                  Comment
                </Button>
              </Flexbox>
            )}
            <div style={{ height: '2rem' }} />
          </Styles.FlexColumn>
        </Styles.FlexContainer>
      ) : (
        <SessionBlockedQnA />
      )}
      {discussionsAPIResponse.map(dr =>
        dr.discussions.map(discussion => {
          const sender = discussion.author;

          const replies = (discussion.replies ?? []).map(reply => {
            const replySender = reply.author;

            return {
              belongsToAuthUser: authUser?.id === replySender?.id,
              discussion: reply,
              authorChip: getChipName(replySender?.id || ''),
            };
          });

          return (
            <Comment
              key={discussion.id}
              authorChip={getChipName(sender?.id || '')}
              belongsToAuthUser={authUser?.id === sender?.id}
              canApprove={canApprove}
              discussion={discussion}
              replies={replies}
              handleReply={handleReply}
              handleDelete={handleDelete}
              mentions={mentions}
              canUserPostComment={canUserPostComment()}
            />
          );
        }),
      )}
      {/* )} */}
      {shouldRenderLoadMoreButton && (
        <ClearButton
          style={{ marginTop: '1rem' }}
          onClick={() => {
            setSize(size + 1);
          }}
        >
          <Text fontWeight="bold">Show more questions</Text>
        </ClearButton>
      )}
      <>
        <PusherHandler
          channel={sessionDiscussionPresentationUpdated.channelName}
          event={sessionDiscussionPresentationUpdated.eventType}
          onUpdate={debounce(handlePusherUpdates, 600)}
        />
        <PusherHandler
          channel={sessionDiscussionPresentationDeleted.channelName}
          event={sessionDiscussionPresentationDeleted.eventType}
          onUpdate={debounce(handlePusherUpdates, 600)}
        />
      </>
    </SimpleCard>
  );
};
